import React, { forwardRef } from 'react';

const BudgetPreview = forwardRef(({ data }, ref) => {
  // A4 dimensions scaled for preview (approximately 0.7 scale)
  const pageStyle = {
    width: '595px',
    minHeight: '842px',
    padding: '0',
    backgroundColor: 'white',
    fontFamily: "'Inter', sans-serif",
    fontSize: '11px',
    lineHeight: '1.5',
    color: '#1a1a2e',
    boxSizing: 'border-box',
    position: 'relative',
    overflow: 'hidden'
  };

  const headerBarStyle = {
    height: '5px',
    backgroundColor: '#2d8a8a',
    width: '100%'
  };

  const tealColor = '#2d8a8a';
  const grayColor = '#6b7280';
  const lightGray = '#f3f4f6';

  return (
    <div ref={ref} className="pdf-preview" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      
      {/* Page 1: Cover Page with Logo and Mascot */}
      <div className="pdf-page" style={pageStyle}>
        <div style={headerBarStyle} />
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '837px',
          padding: '30px'
        }}>
          <img 
            src={data.companyLogo} 
            alt="TuriaDesign" 
            style={{ height: '55px', marginBottom: '30px' }}
            crossOrigin="anonymous"
          />
          <img 
            src={data.mascotImage} 
            alt="Mascot" 
            style={{ width: '180px', height: 'auto' }}
            crossOrigin="anonymous"
          />
        </div>
      </div>

      {/* Page 2: Title Page */}
      <div className="pdf-page" style={pageStyle}>
        <div style={headerBarStyle} />
        <div style={{ padding: '35px 42px' }}>
          {/* Logo at top right */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '55px' }}>
            <img 
              src={data.companyLogo} 
              alt="TuriaDesign" 
              style={{ height: '32px' }}
              crossOrigin="anonymous"
            />
          </div>

          {/* Optional Client Logo */}
          {data.clientLogo && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '28px' }}>
              <img 
                src={data.clientLogo} 
                alt="Client Logo" 
                style={{ height: '55px', maxWidth: '140px', objectFit: 'contain' }}
                crossOrigin="anonymous"
              />
            </div>
          )}

          {/* Document Title */}
          <h1 style={{ 
            fontSize: '22px', 
            fontWeight: '600', 
            color: tealColor, 
            marginBottom: '42px',
            textAlign: 'center',
            lineHeight: '1.3'
          }}>
            {data.documentTitle}
          </h1>

          {/* Project Details */}
          <div style={{ 
            backgroundColor: lightGray, 
            padding: '20px', 
            borderRadius: '6px',
            marginTop: '42px'
          }}>
            <div style={{ marginBottom: '14px', fontSize: '11px' }}>
              <span style={{ color: grayColor, fontWeight: '500' }}>Cliente: </span>
              <span style={{ fontWeight: '600' }}>{data.clientName}</span>
            </div>
            <div style={{ marginBottom: '14px', fontSize: '11px' }}>
              <span style={{ color: grayColor, fontWeight: '500' }}>Proyecto: </span>
              <span style={{ fontWeight: '600' }}>{data.projectName}</span>
            </div>
            <div style={{ fontSize: '11px' }}>
              <span style={{ color: grayColor, fontWeight: '500' }}>Fecha: </span>
              <span style={{ fontWeight: '600' }}>{data.projectDate}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Page 3: Introduction */}
      <div className="pdf-page" style={pageStyle}>
        <div style={headerBarStyle} />
        <div style={{ padding: '35px 42px' }}>
          {/* Logo at top right */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
            <img 
              src={data.companyLogo} 
              alt="TuriaDesign" 
              style={{ height: '28px' }}
              crossOrigin="anonymous"
            />
          </div>

          <h2 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: tealColor, 
            marginBottom: '20px',
            paddingBottom: '8px',
            borderBottom: `2px solid ${tealColor}`
          }}>
            Introducción
          </h2>

          <p style={{ 
            marginBottom: '18px', 
            textAlign: 'justify',
            fontSize: '10px',
            lineHeight: '1.7'
          }}>
            {data.introduction}
          </p>

          <p style={{ 
            textAlign: 'justify',
            fontSize: '10px',
            lineHeight: '1.7'
          }}>
            {data.introductionSecondary}
          </p>
        </div>
      </div>

      {/* Page 4: Modules A & B */}
      <div className="pdf-page" style={pageStyle}>
        <div style={headerBarStyle} />
        <div style={{ padding: '35px 42px' }}>
          {/* Logo at top right */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
            <img 
              src={data.companyLogo} 
              alt="TuriaDesign" 
              style={{ height: '28px' }}
              crossOrigin="anonymous"
            />
          </div>

          <h2 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: tealColor, 
            marginBottom: '8px'
          }}>
            Alcance y Módulos de Desarrollo (A·B)
          </h2>
          <p style={{ color: grayColor, marginBottom: '20px', fontSize: '9px' }}>
            Descripción detallada de cada módulo, con funcionalidades principales así como estimaciones de duración.
          </p>

          {data.modules.slice(0, 2).map((module, index) => (
            <div key={index} style={{ 
              marginBottom: '18px',
              padding: '14px',
              backgroundColor: index === 0 ? '#f0f9f9' : 'white',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: tealColor,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '10px'
                  }}>
                    {module.id}
                  </div>
                  <h3 style={{ fontSize: '11px', fontWeight: '600', margin: 0 }}>
                    Módulo {module.id}: {module.title}
                  </h3>
                </div>
                <span style={{ 
                  backgroundColor: tealColor, 
                  color: 'white', 
                  padding: '3px 8px', 
                  borderRadius: '12px',
                  fontSize: '8px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}>
                  Duración: {module.duration}
                </span>
              </div>
              
              <p style={{ marginBottom: '10px', fontStyle: 'italic', color: '#374151', fontSize: '9px' }}>
                "{module.description}"
              </p>

              {module.features.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: '14px', fontSize: '9px' }}>
                  {module.features.map((feature, fIndex) => (
                    <li key={fIndex} style={{ marginBottom: '5px', color: '#374151' }}>
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Page 5: Modules C & D + Timeline */}
      <div className="pdf-page" style={pageStyle}>
        <div style={headerBarStyle} />
        <div style={{ padding: '35px 42px' }}>
          {/* Logo at top right */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
            <img 
              src={data.companyLogo} 
              alt="TuriaDesign" 
              style={{ height: '28px' }}
              crossOrigin="anonymous"
            />
          </div>

          <h2 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: tealColor, 
            marginBottom: '14px'
          }}>
            Alcance y Módulos de Desarrollo (C·D)
          </h2>

          {data.modules.slice(2, 4).map((module, index) => (
            <div key={index} style={{ 
              marginBottom: '14px',
              padding: '14px',
              backgroundColor: 'white',
              borderRadius: '6px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: tealColor,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '10px'
                  }}>
                    {module.id}
                  </div>
                  <h3 style={{ fontSize: '11px', fontWeight: '600', margin: 0 }}>
                    Módulo {module.id}: {module.title}
                  </h3>
                </div>
                <span style={{ 
                  backgroundColor: tealColor, 
                  color: 'white', 
                  padding: '3px 8px', 
                  borderRadius: '12px',
                  fontSize: '8px',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}>
                  Duración: {module.duration}
                </span>
              </div>
              
              <p style={{ margin: 0, fontStyle: 'italic', color: '#374151', fontSize: '9px' }}>
                "{module.description}"
              </p>
            </div>
          ))}

          {/* Modules Quote */}
          <div style={{ 
            backgroundColor: '#fef3c7', 
            padding: '10px 14px', 
            borderRadius: '6px',
            borderLeft: '3px solid #f59e0b',
            marginBottom: '20px'
          }}>
            <p style={{ margin: 0, fontStyle: 'italic', fontSize: '9px' }}>
              "{data.modulesQuote}"
            </p>
          </div>

          {/* Timeline Section */}
          <h2 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: tealColor, 
            marginBottom: '8px',
            marginTop: '20px'
          }}>
            Cronograma Estimado
          </h2>
          <p style={{ color: grayColor, marginBottom: '14px', fontSize: '9px' }}>
            El cronograma total se estructura en fases para optimizar tiempos y recursos:
          </p>

          {data.timeline.phases.map((phase, index) => (
            <div key={index} style={{ 
              display: 'flex', 
              marginBottom: '10px',
              padding: '10px',
              backgroundColor: lightGray,
              borderRadius: '6px',
              fontSize: '9px'
            }}>
              <div style={{ 
                fontWeight: '700', 
                color: tealColor, 
                minWidth: '55px',
                fontSize: '10px'
              }}>
                {phase.name}:
              </div>
              <div>
                <span>{phase.description}</span>
                <span style={{ fontWeight: '600', marginLeft: '6px' }}>{phase.duration}</span>
                {phase.subItems && (
                  <div style={{ marginTop: '6px', marginLeft: '10px' }}>
                    {phase.subItems.map((item, i) => (
                      <div key={i} style={{ color: '#6b7280' }}>· {item}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Timeline Quote */}
          <div style={{ 
            backgroundColor: '#dbeafe', 
            padding: '10px 14px', 
            borderRadius: '6px',
            borderLeft: '3px solid #3b82f6',
            marginTop: '14px'
          }}>
            <p style={{ margin: 0, fontStyle: 'italic', fontSize: '9px' }}>
              "{data.timeline.quote}"
            </p>
          </div>
        </div>
      </div>

      {/* Page 6: Investment & Next Steps */}
      <div className="pdf-page" style={pageStyle}>
        <div style={headerBarStyle} />
        <div style={{ padding: '35px 42px' }}>
          {/* Logo at top right */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
            <img 
              src={data.companyLogo} 
              alt="TuriaDesign" 
              style={{ height: '28px' }}
              crossOrigin="anonymous"
            />
          </div>

          <h2 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: tealColor, 
            marginBottom: '20px',
            paddingBottom: '8px',
            borderBottom: `2px solid ${tealColor}`
          }}>
            Inversión y Esquema de Pagos
          </h2>

          {/* Investment Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '14px' }}>
            <tbody>
              {data.investment.items.map((item, index) => (
                <tr key={index} style={{ 
                  backgroundColor: index % 2 === 0 ? lightGray : 'white',
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <td style={{ padding: '10px 14px', fontSize: '10px' }}>
                    {item.label}
                  </td>
                  <td style={{ 
                    padding: '10px 14px', 
                    textAlign: 'right', 
                    fontWeight: '700',
                    fontSize: '12px',
                    color: index === 0 ? tealColor : '#1a1a2e'
                  }}>
                    {data.investment.currency} {item.amount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Investment Quote */}
          <div style={{ 
            backgroundColor: '#f0fdf4', 
            padding: '10px 14px', 
            borderRadius: '6px',
            borderLeft: '3px solid #22c55e',
            marginBottom: '28px'
          }}>
            <p style={{ margin: 0, fontStyle: 'italic', fontSize: '9px' }}>
              "{data.investment.quote}"
            </p>
          </div>

          {/* Next Steps */}
          <h2 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: tealColor, 
            marginBottom: '14px',
            paddingBottom: '8px',
            borderBottom: `2px solid ${tealColor}`
          }}>
            Siguientes Pasos
          </h2>

          <p style={{ marginBottom: '14px', fontSize: '10px', lineHeight: '1.7' }}>
            {data.nextSteps.content}
          </p>

          <p style={{ 
            fontSize: '10px', 
            lineHeight: '1.7',
            fontWeight: '500',
            color: tealColor
          }}>
            {data.nextSteps.closing}
          </p>
        </div>
      </div>

      {/* Page 7: Design Images (if any) */}
      {data.designImages.length > 0 && (
        <div className="pdf-page" style={pageStyle}>
          <div style={headerBarStyle} />
          <div style={{ padding: '35px 42px' }}>
            {/* Logo at top right */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '28px' }}>
              <img 
                src={data.companyLogo} 
                alt="TuriaDesign" 
                style={{ height: '28px' }}
                crossOrigin="anonymous"
              />
            </div>

            <h2 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: tealColor, 
              marginBottom: '20px',
              paddingBottom: '8px',
              borderBottom: `2px solid ${tealColor}`
            }}>
              Ejemplos de Diseño / Entregas
            </h2>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: data.designImages.length === 1 ? '1fr' : 'repeat(2, 1fr)', 
              gap: '14px' 
            }}>
              {data.designImages.map((img, index) => (
                <div key={index} style={{ 
                  borderRadius: '6px', 
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                }}>
                  <img 
                    src={img} 
                    alt={`Diseño ${index + 1}`}
                    style={{ 
                      width: '100%', 
                      height: 'auto',
                      display: 'block'
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

BudgetPreview.displayName = 'BudgetPreview';

export default BudgetPreview;
